/* eslint-disable @typescript-eslint/no-unused-vars */
import { globalEvents } from "@/data/Data";
import { Factory } from "@/data/dao/Factory";
import type { ILinkableEntity, IMultiLanguageText } from "@/data/model/Shared";
import type { IPaginatedQry } from "@/data/queries/PaginatedQry";
import { AuthTokenService } from "@/data/services/AuthTokenService";
import { CurrentAppService } from "@/data/services/CurrentAppService";
import { CurrentIdentityService } from "@/data/services/CurrentIdentityService";
import { CurrentLocationService } from "@/data/services/CurrentLocationService";
import { UserSessionService } from "@/data/services/UserSessionService";
import { config, loggingConfig } from "@/data/util/Config";
import { DateTime } from "@/data/util/DateTime";

import { RequestAbortError } from "@/shared/errors/RequestAbortError";

export class DaoError extends Error {
    type: string;
    msg: string;
    additionalData?: any;
    baseContext?: JQueryXHR;
    baseReason?: string;
    callStack?: string;

    constructor(
        type?: string,
        msg?: string,
        additionalData?: any,
        baseContext?: JQueryXHR,
        baseReason?: string,
        callStack?: string
    ) {
        super(msg);

        this.type = type ?? "";
        this.msg = msg ?? "";
        this.additionalData = additionalData;

        this.baseContext = baseContext;
        this.baseReason = baseReason;
        this.callStack = callStack;
    }

    public static fromJson(json: {
        type?: string;
        message?: string;
        additionalData?: any;
        baseContext: JQueryXHR;
        baseReason: string;
        callStack?: string;
    }): DaoError {
        return new DaoError(
            json.type,
            json.message,
            json.additionalData,
            json.baseContext,
            json.baseReason,
            json.callStack
        );
    }
}

export interface IPaginatedResult<T> {
    hasPrev: boolean;
    hasNext: boolean;
    offset: number;
    limit: number;
    items: Array<T>;
}

export interface IDataSource<T> {
    getData(filter: IPaginatedQry, abortSignal?: AbortSignal): Promise<IPaginatedResult<T>>;
}

export interface IEditableDataSource<T, S = T | any> extends IDataSource<T> {
    getSingle(id: string, abortSignal?: AbortSignal): Promise<S>;

    create(data: any, abortSignal?: AbortSignal): Promise<S>;

    update(id: string, data: any, abortSignal?: AbortSignal): Promise<S>;

    remove(id: string, abortSignal?: AbortSignal): Promise<void>;
}

export interface IImageDataSource<T> {
    valueAccessor(t: T, setter: string | undefined | null): string | undefined;

    create(t: T, file: File, abortSignal?: AbortSignal): Promise<string>;

    remove(t: T, abortSignal?: AbortSignal): Promise<void>;
}

export interface IMultiEntityLinkDataSource {
    search(excludeIds: Array<string>, term?: string, abortSignal?: AbortSignal): Promise<Array<ILinkableEntity>>;
}

export interface IResponseParser<T> {
    (l: any): T | PromiseLike<T>;
}

export abstract class BaseServiceDao {
    protected basePath: string;
    protected isLocationDependent = false;

    constructor(path: string) {
        this.basePath = "/api/" + path;
    }

    protected get<T>(path: string, query?: object, parser?: IResponseParser<T>, abortSignal?: AbortSignal): Promise<T> {
        return this.doXHR(path, "GET", query, parser, false, abortSignal);
    }

    protected post<T>(
        path: string,
        cmd?: object,
        parser?: IResponseParser<T>,
        isMultiPart?: boolean,
        abortSignal?: AbortSignal
    ): Promise<T> {
        return this.doXHR(path, "POST", cmd, parser, isMultiPart, abortSignal);
    }

    protected put<T>(
        path: string,
        cmd?: object,
        parser?: IResponseParser<T>,
        isMultiPart?: boolean,
        abortSignal?: AbortSignal
    ): Promise<T> {
        return this.doXHR(path, "PUT", cmd, parser, isMultiPart, abortSignal);
    }

    protected delete<T>(
        path: string,
        query?: object,
        parser?: IResponseParser<T>,
        abortSignal?: AbortSignal
    ): Promise<T> {
        return this.doXHR(path, "DELETE", query, parser, false, abortSignal);
    }

    protected postDownloadFile(path: string, data?: object, abortSignal?: AbortSignal): Promise<void> {
        const callStack = this.getCallStack();
        const baseServiceDao = this;

        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();

            //add timestamp to path
            path += (path.includes("?") ? "&" : "?") + "c=" + new Date().getTime();

            if (data === undefined) data = {};

            xhr.open("POST", this.basePath + "/" + path, true);
            xhr.responseType = "arraybuffer";
            xhr.onerror = () => {
                reject();
            };

            xhr.onload = function () {
                if (this.status === 200) {
                    let filename = "";
                    const disposition = xhr.getResponseHeader("Content-Disposition");
                    if (disposition && disposition.indexOf("attachment") !== -1) {
                        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                        const matches = filenameRegex.exec(disposition);
                        if (matches != null && matches[1]) filename = matches[1].replace(/['"]/g, "");
                    }
                    const type = xhr.getResponseHeader("Content-Type");

                    let blob;
                    if (typeof File === "function") {
                        try {
                            blob = new File([this.response], filename, { type: type === null ? undefined : type });
                        } catch (e) {
                            /* Edge */
                        }
                    }

                    if (typeof blob === "undefined") {
                        blob = new Blob([this.response], { type: type === null ? undefined : type });
                    }

                    if (typeof window.navigator["msSaveBlob"] !== "undefined") {
                        window.navigator["msSaveBlob"](blob, filename);
                        resolve();
                    } else {
                        const URL = window.URL || window.webkitURL;
                        const downloadUrl = URL.createObjectURL(blob);

                        if (filename) {
                            const a = document.createElement("a");
                            if (typeof a.download === "undefined") window.location.href = downloadUrl;
                            else {
                                a.href = downloadUrl;
                                a.download = filename;
                                document.body.appendChild(a);
                                a.click();
                            }
                        } else window.location.href = downloadUrl;

                        setTimeout(function () {
                            URL.revokeObjectURL(downloadUrl);
                            resolve();
                        }, 100);
                    }
                } else {
                    const daoError = baseServiceDao.getFileDaoError(this, callStack);
                    reject(daoError);
                }
            };
            xhr.setRequestHeader("Content-type", "application/json");
            this.setAdditionalHeaders(xhr, data === undefined || data == null || data["locationId"] === undefined);

            // cancellation token
            if (abortSignal) {
                abortSignal.addEventListener("abort", () => {
                    xhr.abort();
                });
            }

            xhr.send(JSON.stringify(this.formatParamsRecursive(data)));
        });
    }

    private doXHR<T>(
        serviceUrl: string,
        requestType: string,
        params?: object,
        parser?: IResponseParser<T>,
        isMultipart?: boolean,
        abortSignal?: AbortSignal
    ): Promise<T> {
        if (isMultipart === undefined) isMultipart = false;

        let requestOptions: JQueryAjaxSettings;
        const callStack = this.getCallStack();

        const prom = new Promise<T>((resolve, reject) => {
            requestOptions = {
                url: this.basePath + "/" + serviceUrl,
                dataType: "json",
                contentType: "application/json; charset=utf-8",
                type: requestType,
                traditional: true,
                beforeSend: (xhr) => {
                    // cancellation token
                    if (abortSignal) {
                        abortSignal.addEventListener("abort", () => {
                            xhr.abort();
                        });
                    }

                    this.setAdditionalHeaders(
                        xhr,
                        params === undefined || params == null || params["locationId"] === undefined
                    );
                },
                success: (data) => {
                    if (data !== undefined) {
                        if (parser !== undefined) resolve(parser(data));
                        else resolve(data);
                    } else resolve(undefined as T);
                },
                error: (context, reason) => {
                    // if the request was aborted, reject the promise
                    if (reason == "abort") {
                        reject(new RequestAbortError());
                        return;
                    }

                    if ((context.status === 200 || context.status === 201) && context.responseText === "") {
                        resolve(undefined as T);
                        return;
                    }

                    console.warn(`Network error, Reason: ${reason}, Status: ${context.statusText} ${context.status}`);

                    if (context.status === 401 || context.status === 403) {
                        globalEvents.OnSessionExpired.dispatch();
                    }

                    const daoError = this.getDaoError(context, reason, callStack);
                    reject(daoError);
                    return;
                },
            };

            if (isMultipart) {
                requestOptions["data"] = BaseServiceDao.convertMultiPart(params);
                requestOptions["contentType"] = false;
                requestOptions["processData"] = false;
                requestOptions["dataType"] = undefined;
            } else if (params !== undefined && params != null && !isMultipart) {
                requestOptions["data"] =
                    requestType === "DELETE" || requestType === "POST" || requestType === "PUT"
                        ? JSON.stringify(this.formatParamsRecursive(params))
                        : params;
            }

            $.ajax(requestOptions);
        });

        return prom;
    }

    public static getClassName(): string {
        if (this["className"] === undefined) {
            const funcNameRegex = /function (.{1,})\(/;
            const results = funcNameRegex.exec(this.toString());
            this["className"] = results && results.length > 1 ? results[1] : "";
        }

        return this["className"];
    }

    private formatParamsRecursive(params): any {
        const type = typeof params;
        if (Array.isArray(params)) {
            const arr: any[] = [];
            for (const i in params) arr.push(this.formatParamsRecursive(params[i]));

            return arr;
        } else if (params instanceof Date) {
            return DateTime.serializeIsoDate(params);
        } else if (type == "object") {
            const mapping = {};
            for (const i in params) mapping[i] = this.formatParamsRecursive(params[i]);

            return mapping;
        } else return params;
    }

    private setAdditionalHeaders(xhr, addLocation?: boolean) {
        if (this.isLocationDependent && (addLocation === undefined || addLocation === true)) {
            const locationId = CurrentLocationService.getLocationId();
            if (locationId) {
                xhr.setRequestHeader("Location", locationId);
            }
        }

        const authTokenService = Factory.getInstance<AuthTokenService>(AuthTokenService);
        const bearerToken = authTokenService.getBearer();
        if (bearerToken !== undefined) {
            xhr.setRequestHeader("Authorization", `Bearer ${bearerToken}`);
        }

        if (!loggingConfig.logSessionId) {
            return;
        }

        const userSessionService = Factory.getInstance<UserSessionService>(UserSessionService);
        const sessionId = userSessionService.getSessionId();
        if (sessionId) {
            xhr.setRequestHeader("x-app-session-id", sessionId);
        }

        if (config.buildNumber) {
            xhr.setRequestHeader("x-build", config.buildNumber);
        }

        const identityId = CurrentIdentityService.getIdentityId();
        if (identityId) {
            xhr.setRequestHeader("x-identity", identityId);
        }

        const nativeAppMode = CurrentAppService.getNativeAppMode();
        xhr.setRequestHeader("x-native-app-mode", nativeAppMode);

        const isWalleeSdkAvailable = CurrentAppService.getIsWalleeSdkAvailable();
        xhr.setRequestHeader("x-is-wallee-sdk-available", isWalleeSdkAvailable);
    }

    public static convertMultiPart(data: any): FormData {
        const mp = new FormData();
        for (const i in data) {
            const value = data[i];
            if (value === undefined || value == null) continue;

            if (Array.isArray(value)) {
                let ai = 0;
                for (const a in value) {
                    mp.append(i + "[" + ai + "]", value[a]);
                    ai++;
                }

                continue;
            } else if (value instanceof Date) {
                mp.append(i, DateTime.serializeIsoDate(value));
                continue;
            }

            mp.append(i, value);
        }

        return mp;
    }

    private getFileDaoError(context: XMLHttpRequest, callStack: any) {
        if (context.status < 400) return undefined;

        let pj;
        try {
            const enc = new TextDecoder("utf-8");
            const decodedResponse = enc.decode(context.response);
            pj = decodedResponse !== undefined ? JSON.parse(decodedResponse) : {};
        } catch (e) {
            pj = {};
        }

        pj = {
            ...pj,
            baseContext: context,
            baseReason: undefined,
            callStack: callStack,
        };

        const daoError = DaoError.fromJson(pj);
        return daoError;
    }

    private getDaoError(context: JQueryXHR, reason: string, callStack: any) {
        let pj;
        try {
            pj = context.responseText !== undefined ? JSON.parse(context.responseText) : {};
        } catch (e) {
            pj = {};
        }

        pj = {
            ...pj,
            baseContext: context,
            baseReason: reason,
            callStack: callStack,
        };

        const daoError = DaoError.fromJson(pj);
        return daoError;
    }

    private getCallStack() {
        try {
            throw new Error("Generating call stack");
        } catch (e) {
            try {
                let stack = ((e as any)?.stack ?? "").toString();
                stack = stack
                    .split("\n") // Split stack trace into lines
                    .slice(3) // Remove the first three lines
                    .map((line) => line.trim()) // Trim leading and trailing spaces from each line
                    .join("\n"); // Join the remaining lines back into a single string

                return stack;
            } catch {
                return "";
            }
        }
    }
}

export function parseCollection<T>(literal: any, handler: { (literal: any): T }): Array<T> {
    const arr = new Array<T>();
    for (const i in literal) {
        arr.push(handler(literal[i]));
    }

    return arr;
}

export function parseMultiLanguageText(literal?: any): IMultiLanguageText {
    if (literal === undefined || literal == null) return {};

    return {
        de: literal.de == null ? undefined : literal.de,
        en: literal.en == null ? undefined : literal.en,
        fr: literal.fr == null ? undefined : literal.fr,
        it: literal.it == null ? undefined : literal.it,
    };
}

export function parsePaginatedResult<T>(literal: any, handler: { (literal: any): T }): IPaginatedResult<T> {
    return {
        hasNext: literal.hasNext,
        hasPrev: literal.hasPrev,
        limit: parseInt(literal.limit),
        offset: parseInt(literal.offset),
        items: parseCollection(literal.items, handler),
    };
}
